import type { IInvoiceManualPaymentType } from '@manager/api/types/invoice-types';
import type { IAllowedPaymentMethod } from '@manager/api/types/payment-method-types';
import { colors } from '@manager/config/color-config';

export const paymentMethodsRecord: Record<IAllowedPaymentMethod, string> = {
  card: 'Card',
  sepa_debit: 'SEPA via Stripe',
  sepa_bank: 'SEPA via XML Export',
  sofort: 'SOFORT',
  klarna: 'Klarna',
  giropay: 'Giropay',
  ideal: 'iDEAL',
  link: 'Link',
  bancontact: 'Bancontact',
  acss_debit: 'ACSS Debit',
  affirm: 'Affirm',
  afterpay_clearpay: 'Afterpay Clearpay',
  au_bacs_debit: 'AU BECS Debit',
  bacs_debit: 'Bacs Debit',
  blik: 'Blik',
  boleto: 'Boleto',
  cashapp: 'Cash App',
  customer_balance: 'Stripe customer balance',
  eps: 'EPS',
  fpx: 'FPX',
  grabpay: 'GrabPay',
  konbini: 'Konbini',
  p24: 'P24',
  oxxo: 'OXXO',
  paynow: 'PayNow',
  paypal: 'PayPal',
  pix: 'PIX',
  promptpay: 'PromptPay',
  revolut_pay: 'Revolut Pay',
  us_bank_account: 'US Bank Account',
  wechat_pay: 'WeChat Pay',
  zip: 'Zip',
  twint: 'TWINT',
};

export const creditCards = [
  'mastercard',
  'visa',
  'amex',
  'unionpay',
  'diners',
  'discover',
  'cartesbancaires',
  'jcb',
] as const;

export type CreditCard = (typeof creditCards)[number];

export const creditCardsRecord: Record<CreditCard, string> = {
  mastercard: 'Mastercard',
  visa: 'Visa',
  amex: 'Amex',
  unionpay: 'UnionPay',
  diners: 'Diners Club',
  discover: 'Discover',
  cartesbancaires: 'CB',
  jcb: 'JCB',
};

export const basePaymentMethods = ['card', 'sepa_debit', 'bacs_debit'] as const;
export type IBasePaymentMethod = (typeof basePaymentMethods)[number];
export const sepaBankMethod = 'sepa_bank';
export type IAllPaymentMethods = IBasePaymentMethod | typeof sepaBankMethod;

export interface PaymentBrand {
  card: Record<CreditCard, PaymentData>;
  other: {
    sepa_debit: PaymentData;
    sepa_bank: PaymentData;
    sofort: PaymentData;
    ideal: PaymentData;
    bancontact: PaymentData;
    bacs_debit: PaymentData;
    customer_balance: PaymentData;
    cash: PaymentData;
    wire_transfer: PaymentData;
    card_onsite: PaymentData;
  };
  sepa_debit: PaymentData;
  custom: PaymentData;
}

export interface PaymentData {
  icon: string | null;
  title: string;
  translation_key?: string;
}

export const paymentBrands: PaymentBrand = {
  card: {
    mastercard: {
      title: 'Card',
      icon: 'mastercard',
    },
    visa: {
      title: 'Card',
      icon: 'visa',
    },
    amex: {
      title: 'Card',
      icon: 'amex',
    },
    unionpay: {
      title: 'Card',
      icon: 'unionpay',
    },
    diners: {
      title: 'Card',
      icon: 'diners',
    },
    discover: {
      title: 'Card',
      icon: 'discover',
    },
    cartesbancaires: {
      title: 'Card',
      icon: 'cartesbancaires',
    },
    jcb: {
      title: 'Card',
      icon: 'jcb',
    },
  },
  other: {
    sepa_debit: {
      title: 'SEPA via Stripe',
      icon: 'sepa',
      translation_key: 'paymentTypes.sepa_debit',
    },
    sepa_bank: {
      title: 'SEPA via XML Export',
      icon: 'sepa',
      translation_key: 'paymentTypes.sepa_bank',
    },
    sofort: {
      title: 'Sofort',
      icon: 'sofort',
    },
    ideal: {
      title: 'iDEAL',
      icon: 'ideal',
    },
    bancontact: {
      title: 'Bancontact',
      icon: 'bancontact',
    },
    bacs_debit: {
      title: 'Bacs Direct Debit',
      icon: 'bacs_debit',
    },
    customer_balance: {
      title: 'Stripe balance',
      icon: 'stripe',
    },
    cash: {
      title: 'Cash',
      icon: null,
      translation_key: 'paymentTypes.cash',
    },
    wire_transfer: {
      title: 'Wire Transfer',
      icon: null,
      translation_key: 'paymentTypes.wire_transfer',
    },
    card_onsite: {
      title: 'Card Onsite',
      icon: null,
      translation_key: 'paymentTypes.card_onsite',
    },
  },
  /**
   * @deprecated use `paymentBrands.other.sepa_debit`
   */
  sepa_debit: {
    title: 'SEPA',
    icon: 'sepa',
  },
  custom: {
    title: 'Custom',
    icon: null,
  },
};

export const stripePaymentMethodTypes: (IAllowedPaymentMethod | string)[] = [
  'card',
  'sepa_debit',
  'bacs_debit',
  'sofort',
  'bancontact',
  'ideal',
];
export const manualPaymentTypes: (IInvoiceManualPaymentType | string)[] = [
  'cash',
  'wire_transfer',
  'card_onsite',
  'custom',
];

export const supportedCountries = ['SEPA'];

export const stripeElementStyles = {
  base: {
    color: '#32325D',
    fontWeight: 300,
    fontFamily: 'Inter, sans-serif',
    fontSize: '16px',
    '::placeholder': {
      color: 'rgba(0, 0, 0, 0.42)',
    },
    ':-webkit-autofill': {
      color: 'rgba(0, 0, 0, 0.42)',
    },
  },
  invalid: {
    color: colors.app.danger,
    fontWeight: 300,
    fontFamily: 'Inter, sans-serif',
    fontSize: '16px',
    '::placeholder': {
      color: colors.app.danger,
    },
  },
};

export const stripeElementClasses = {
  focus: 'focused',
  empty: 'empty',
  invalid: 'invalid',
};
